import { Component, Host, h, getAssetPath, Prop, Event, EventEmitter, Method, State, Listen, Watch } from '@stencil/core';
import { Consumes } from '@sydle/sydle-ui';
import { makeContextAvailability } from '../../utils/contextsAvailability';

/**
 * TODO
 * - list of supported social networks
 * - fields to use in email default phase
 */
@Component({
  tag: 'sy-site-sign-in-wall',
  styleUrl: './scss/index.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class SydleSiteSignInWall {
  private contextsAvailability = makeContextAvailability(['t', 'lang']);

  @Consumes('i18next:t') @State() t;
  @Consumes('i18next:language') @State() lang: string;

  private emailSignInBtn?: HTMLElement;
  private socialMediaButtons?: HTMLElement;
  private dialog: HTMLSyDialogElement;

  private nameInputValue: any = null;
  private emailInputValue: any = null;

  /**
   * Email token value
   */
  @State() emailTokenInputValue: any = null;

  /** */
  @State() errMsg: string;

  /** */
  @State() errMsgName: string;

  /**
   * Defines if the sign-in wall not supports email signin
   */
  @Prop() noEmail = false;

  /**
   * Defines the fase os email signin between initial information or token
   */
  @State() emailPhase: 'default' | 'token' | 'error' = 'default';

  /**
   * Controls if sign in buttons are disabled or not
   */
  @State() disabled = false;

  /**
   * Supported social networks list
   */
  @Prop() supportedNetworks: Array<string>;

  /**
   * Emmitted when some login button is pressed
   */
  @Event({ bubbles: true }) sySiteLoginDidSubmit: EventEmitter<{
    type: 'social' | 'email' | 'token';
    data: any;
  }>;

  /**
   * Emmitted when sy-dialog is closed
   */
  @Event({ bubbles: true, composed: true }) sySiteLoginDidClose: EventEmitter;

  /**
   * Emmitted when sy-dialog is open
   */
  @Event({ bubbles: true }) sySiteLoginDidOpen: EventEmitter;

  @Watch('t')
  @Watch('lang')
  setContexts(): void {
    if (this.t) {
      this.contextsAvailability.props.t.resolve();
    }
    if (this.lang) {
      this.contextsAvailability.props.lang.resolve();
    }
  }

  /**
   * Always disable sign in buttons after clicking to avoid multiple event emissions.
   * Sign in steps following event emission should be handled by component user.
   */
  @Listen('sySiteLoginDidSubmit')
  submitHandler(): void {
    this.disabled = true;
  }

  @Listen('syDialogDidClose')
  closeComponent(): void {
    this.changeEmailPhase('default');
    this.sySiteLoginDidClose.emit();
  }

  /**
   * Opens the login dialog
   */
  @Method()
  async open(): Promise<void> {
    this.dialog.open();
    this.sySiteLoginDidOpen.emit();
  }

  /**
   * Closes the login dialog
   */
  @Method()
  async close(): Promise<void> {
    this.dialog.close();
    this.changeEmailPhase('default');
    this.sySiteLoginDidClose.emit();
  }

  /**
   * Changes email phase and enables signing in
   */
  @Method()
  async changeEmailPhase(emailPhase: 'default' | 'token' | 'error'): Promise<void> {
    this.emailPhase = emailPhase;
    (this.emailSignInBtn.querySelector('.button-text') as HTMLElement).style.display = 'inline';
    (this.emailSignInBtn.querySelector('.sy-loader-ellipsis') as HTMLElement).style.display = 'none';
    this.disabled = false;
  }

  /**
   * Set Error
   */
  @Method()
  async setError(msg?: string, msgName?: string): Promise<void> {
    msg ? (this.errMsg = msg) : null;
    msgName ? (this.errMsgName = msgName) : null;
  }

  private handleSocialLogin = (socialNetwork: string) => {
    if (this.disabled) return;
    this.socialMediaButtons.querySelectorAll(`.btn-${socialNetwork} .button-text`).forEach((el: HTMLElement) => (el.style.display = 'none'));
    this.socialMediaButtons.querySelectorAll(`.btn-${socialNetwork} .sy-loader-ellipsis`).forEach((el: HTMLElement) => (el.style.display = 'inline-block'));
    this.sySiteLoginDidSubmit.emit({
      type: 'social',
      data: socialNetwork,
    });
  };

  private handleEmailLogin = () => {
    if (this.disabled) return;
    let eventObj;
    if (this.emailPhase === 'default') {
      const emailRegex = /^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})$/gm;
      const validEmail = emailRegex.test(this.emailInputValue);
      if (validEmail && this.nameInputValue) {
        (this.emailSignInBtn.querySelector('.button-text') as HTMLElement).style.display = 'none';
        (this.emailSignInBtn.querySelector('.sy-loader-ellipsis') as HTMLElement).style.display = 'inline-block';
        eventObj = {
          type: 'email',
          data: {
            name: this.nameInputValue,
            email: this.emailInputValue,
          },
        };
      } else {
        if (!this.nameInputValue) {
          this.setError('', this.t('NAME_ERROR'));
        }
        if (!validEmail) {
          this.setError(this.t('EMAIL_ERROR'));
        }
        return;
      }
    } else if (this.emailPhase === 'token') {
      eventObj = {
        type: 'token',
        data: this.emailTokenInputValue,
      };
    }
    this.sySiteLoginDidSubmit.emit(eventObj);
  };

  private elipseLoader(style = { display: 'none' }) {
    return (
      <div class="sy-loader-ellipsis" style={style}>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    );
  }

  private getTitle() {
    if (this.emailPhase === 'error') {
      return 'ERROR_CONTINUE';
    } else if (this.emailPhase === 'token') {
      return 'TOKEN_CONTINUE';
    } else {
      return 'LOGIN_CONTINUE';
    }
  }

  async componentWillRender() {
    this.setContexts();
    await this.contextsAvailability.all;
  }

  render() {
    const privacyPolicyText = this.t('PRIVACY_POLICY_TEXT', {
      policy: `<a href="${this.t('PRIVACY_POLICY_LINK')}">${this.t('POLICY')}</a>`,
      interpolation: { escapeValue: false },
    });

    return (
      <Host>
        <sy-dialog
          ref={(el: HTMLSyDialogElement) => (this.dialog = el)}
          hide-footer
          hide-close
          onSyDialogDidClose={() => {
            this.closeComponent();
          }}
        >
          <div class="social-media-overlay">
            <div class="title">
              <sy-text type="h2">{this.t(this.getTitle())}</sy-text>
            </div>

            {this.emailPhase === 'default' ? (
              <div ref={el => (this.socialMediaButtons = el as HTMLElement)} class="social-media-buttons">
                {this.supportedNetworks?.includes('linkedin') && (
                  <sy-button disabled={this.disabled ? true : undefined} onClick={() => this.handleSocialLogin('linkedin')} class="btn-linkedin">
                    <div class="button-content">
                      <span class="button-icon">
                        <img src={getAssetPath('./assets/icon-login-linkedin.svg')} alt="Linkedin" />
                      </span>
                      <div class="absolute-content">
                        <sy-text class="button-text" type="body">
                          LinkedIn
                        </sy-text>
                        {this.elipseLoader()}
                      </div>
                    </div>
                  </sy-button>
                )}

                {this.supportedNetworks?.includes('google') && (
                  <sy-button disabled={this.disabled ? true : undefined} onClick={() => this.handleSocialLogin('google')} class="btn-google">
                    <div class="button-content">
                      <div class="button-icon">
                        <img src={getAssetPath('./assets/icon-login-google.svg')} alt="Google" />
                      </div>
                      <div class="absolute-content">
                        <sy-text class="button-text" type="body">
                          Google
                        </sy-text>
                        {this.elipseLoader()}
                      </div>
                    </div>
                  </sy-button>
                )}

                {this.supportedNetworks?.includes('facebook') && (
                  <sy-button disabled={this.disabled ? true : undefined} onClick={() => this.handleSocialLogin('facebook')} class="btn-facebook">
                    <div class="button-content">
                      <div class="button-icon">
                        <img src={getAssetPath('./assets/icon-login-facebook.svg')} alt="Facebook" />
                      </div>
                      <div class="absolute-content">
                        <sy-text class="button-text" type="body">
                          Facebook
                        </sy-text>
                        {this.elipseLoader()}
                      </div>
                    </div>
                  </sy-button>
                )}
              </div>
            ) : null}

            {!this.noEmail && this.emailPhase !== 'error' ? (
              <div class="form">
                {this.emailPhase === 'default' ? (
                  <div>
                    <div class="divider">{this.t('CONTINUE_WITH_EMAIL')}</div>
                    <sy-input
                      name="name"
                      onSyInputDidChange={e => (this.nameInputValue = e.detail.value)}
                      label={this.t('FULL_NAME')}
                      value={this.nameInputValue}
                      error={this.errMsgName}
                    ></sy-input>
                    <sy-input
                      onSyInputDidChange={e => (this.emailInputValue = e.detail.value)}
                      type="email"
                      name="email"
                      label="E-mail"
                      error={this.errMsg}
                      value={this.emailInputValue}
                    ></sy-input>
                  </div>
                ) : (
                  <div class="token-container">
                    <sy-text type="body">{this.t('TOKEN_MESSAGE')}</sy-text>
                    <sy-site-otp-input onSySiteOtpInputDidChange={(e: CustomEvent) => (this.emailTokenInputValue = e.detail.value)} value={this.emailTokenInputValue} />
                  </div>
                )}
                <sy-button
                  ref={el => (this.emailSignInBtn = el as HTMLElement)}
                  disabled={this.disabled || (this.emailPhase === 'token' && !this.emailTokenInputValue) ? true : undefined}
                  type="accent"
                  onClick={this.handleEmailLogin}
                >
                  <sy-text class="button-text" style={{ display: this.disabled ? 'none' : 'inline' }}>
                    {this.t('BUTTON_LOGIN_CONTINUE')}
                  </sy-text>
                  {this.elipseLoader({ display: this.disabled && this.emailPhase === 'token' ? 'inline-block' : 'none' })}
                </sy-button>
              </div>
            ) : (
              <div class="error-container">
                <sy-text type="body">{this.errMsg}</sy-text>
                <sy-button
                  type="accent"
                  onClick={() => {
                    this.setError(null);
                    this.changeEmailPhase('default');
                  }}
                >
                  <sy-text class="button-text">{this.t('BUTTON_LOGIN_CONTINUE')}</sy-text>
                </sy-button>
              </div>
            )}
            {this.emailPhase !== 'error' && (
              <div class="content">
                <slot name="content">
                  <div class="privacy-policy">
                    <div class="terms-and-conditions">
                      <sy-text type="small">{this.t('EMAIL_RECEIVEMENT')}</sy-text>
                    </div>
                    <div>
                      <sy-text type="small" innerHTML={privacyPolicyText}></sy-text>
                    </div>
                  </div>
                </slot>
              </div>
            )}
          </div>
          <div style={{ display: 'none' }}>
            <slot></slot>
          </div>
        </sy-dialog>
      </Host>
    );
  }
}
